/**
 * A unified line diff for the snippet history panel — `PG-IDE05`.
 *
 * Deliberately small, and deliberately not Monaco's `DiffEditor`: the question
 * a version picker answers is "what did this save change", which a compact
 * unified list answers in the space a dropdown has. A second full editor for a
 * preview would cost more chrome than the answer is worth, and a diff library
 * would be a dependency for one screen.
 *
 * Line-granular, because a version is a save and a save is edited in lines. The
 * algorithm is the standard longest-common-subsequence walk, which is O(n * m)
 * in lines; the inputs are bounded by the snippet source ceiling (50,000
 * characters, so a few thousand lines at worst), and {@link MAX_DIFF_LINES}
 * refuses anything past that rather than freezing the tab.
 */

export type SnippetDiffKind = "same" | "added" | "removed";

export interface SnippetDiffLine {
  kind: SnippetDiffKind;
  text: string;
  /** 1-based line number in the old text, `null` for an added line. */
  oldLine: number | null;
  /** 1-based line number in the new text, `null` for a removed line. */
  newLine: number | null;
}

export interface SnippetDiff {
  lines: SnippetDiffLine[];
  added: number;
  removed: number;
  /** True when the inputs were too large to diff and only the counts are real. */
  toobig: boolean;
}

/**
 * Lines either side may hold before the diff refuses.
 *
 * At the 50,000-character source ceiling a pathological file of single-character
 * lines reaches 25,000, and the LCS table for two of those is 625 million cells:
 * an unresponsive tab, not a diff. Ordinary code is two orders of magnitude
 * under this.
 */
export const MAX_DIFF_LINES = 3_000;

function splitLines(text: string): string[] {
  if (text === "") return [];
  return text.replace(/\r\n?/g, "\n").split("\n");
}

/**
 * Trim the common head and tail before the table is built.
 *
 * A save usually changes a handful of lines in the middle of a file, so this is
 * what keeps the quadratic part small in the case that actually happens.
 */
function commonEdges(a: readonly string[], b: readonly string[]): { head: number; tail: number } {
  let head = 0;
  const max = Math.min(a.length, b.length);
  while (head < max && a[head] === b[head]) head += 1;
  let tail = 0;
  while (tail < max - head && a[a.length - 1 - tail] === b[b.length - 1 - tail]) tail += 1;
  return { head, tail };
}

function lcsTable(a: readonly string[], b: readonly string[]): Uint32Array {
  const width = b.length + 1;
  const table = new Uint32Array((a.length + 1) * width);
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i * width + j] =
        a[i] === b[j]
          ? table[(i + 1) * width + (j + 1)]! + 1
          : Math.max(table[(i + 1) * width + j]!, table[i * width + (j + 1)]!);
    }
  }
  return table;
}

/** The diff from `oldText` to `newText`, oldest state first. */
export function diffSnippetSources(oldText: string, newText: string): SnippetDiff {
  const a = splitLines(oldText);
  const b = splitLines(newText);

  if (a.length > MAX_DIFF_LINES || b.length > MAX_DIFF_LINES) {
    return { lines: [], added: b.length, removed: a.length, toobig: true };
  }

  const { head, tail } = commonEdges(a, b);
  const midA = a.slice(head, a.length - tail);
  const midB = b.slice(head, b.length - tail);

  const lines: SnippetDiffLine[] = [];
  let oldLine = 1;
  let newLine = 1;
  for (let i = 0; i < head; i += 1) {
    lines.push({ kind: "same", text: a[i]!, oldLine: oldLine++, newLine: newLine++ });
  }

  const width = midB.length + 1;
  const table = lcsTable(midA, midB);
  let i = 0;
  let j = 0;
  let added = 0;
  let removed = 0;
  while (i < midA.length && j < midB.length) {
    if (midA[i] === midB[j]) {
      lines.push({ kind: "same", text: midA[i]!, oldLine: oldLine++, newLine: newLine++ });
      i += 1;
      j += 1;
    } else if (table[(i + 1) * width + j]! >= table[i * width + (j + 1)]!) {
      lines.push({ kind: "removed", text: midA[i]!, oldLine: oldLine++, newLine: null });
      removed += 1;
      i += 1;
    } else {
      lines.push({ kind: "added", text: midB[j]!, oldLine: null, newLine: newLine++ });
      added += 1;
      j += 1;
    }
  }
  while (i < midA.length) {
    lines.push({ kind: "removed", text: midA[i]!, oldLine: oldLine++, newLine: null });
    removed += 1;
    i += 1;
  }
  while (j < midB.length) {
    lines.push({ kind: "added", text: midB[j]!, oldLine: null, newLine: newLine++ });
    added += 1;
    j += 1;
  }

  for (let k = 0; k < tail; k += 1) {
    lines.push({
      kind: "same",
      text: a[a.length - tail + k]!,
      oldLine: oldLine++,
      newLine: newLine++,
    });
  }

  return { lines, added, removed, toobig: false };
}

/**
 * The diff with long unchanged stretches collapsed to `context` lines each side.
 *
 * The panel shows what moved, not the file: a hundred untouched lines between
 * two edits are one "N unchanged lines" marker, which is the difference between
 * a readable answer and a scroll.
 */
export function collapseUnchanged(
  lines: readonly SnippetDiffLine[],
  context = 2,
): (SnippetDiffLine | { kind: "gap"; hidden: number })[] {
  const out: (SnippetDiffLine | { kind: "gap"; hidden: number })[] = [];
  let run: SnippetDiffLine[] = [];

  const flush = (atEnd: boolean) => {
    if (run.length === 0) return;
    const leading = out.length === 0 ? 0 : context;
    const trailing = atEnd ? 0 : context;
    if (run.length <= leading + trailing + 1) {
      out.push(...run);
    } else {
      out.push(...run.slice(0, leading));
      out.push({ kind: "gap", hidden: run.length - leading - trailing });
      if (trailing > 0) out.push(...run.slice(run.length - trailing));
    }
    run = [];
  };

  for (const line of lines) {
    if (line.kind === "same") {
      run.push(line);
      continue;
    }
    flush(false);
    out.push(line);
  }
  flush(true);
  return out;
}
